'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check, ArrowRight, Info } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { PricingComparisonTable } from './pricing-comparison-table'
import type { PricingTier, BuddifyRoleConfig } from '@/content/data/pricing-hub'

interface AppPricingSectionProps {
  app: 'buddify' | 'ai-assistant'
  title: string
  description?: string
  tiers: PricingTier[]
  roles?: BuddifyRoleConfig[]
  defaultRole?: string
  defaultPeriod?: BillingPeriod
  showPeriodSwitcher?: boolean
  variant?: 'primary' | 'ai'
}

type BillingPeriod = 'monthly' | 'yearly'

function formatPrice(price: number) {
  return `$${price}`
}

export function AppPricingSection({
  app,
  title,
  description,
  tiers,
  roles,
  defaultRole,
  defaultPeriod = 'monthly',
  showPeriodSwitcher = true,
  variant = app === 'ai-assistant' ? 'ai' : 'primary',
}: AppPricingSectionProps) {
  const [period, setPeriod] = useState<BillingPeriod>(defaultPeriod)
  const [activeRole, setActiveRole] = useState(
    defaultRole || roles?.[0]?.key || 'supplier'
  )

  const isAi = variant === 'ai'

  const activeTiers = roles
    ? roles.find((r) => r.key === activeRole)?.tiers || roles[0].tiers
    : tiers

  return (
    <Section background={isAi ? 'dark' : 'gray'}>
      <Container>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h1
            className={cn(
              'text-display-1 mb-4',
              'text-ink'
            )}
          >
            {title}
          </h1>
          {description && (
            <p
              className={cn(
                'text-body-lg max-w-2xl mx-auto',
                'text-muted'
              )}
            >
              {description}
            </p>
          )}
        </motion.div>

        {/* Controls: role selector on the left, billing on the right */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12 max-w-5xl mx-auto"
        >
          {roles && roles.length > 0 && (
            <SegmentedControl
              value={activeRole}
              onChange={setActiveRole}
              options={roles.map((r) => ({ value: r.key, label: r.label }))}
              variant={variant}
            />
          )}

          {showPeriodSwitcher && (
            <SegmentedControl
              value={period}
              onChange={(v) => setPeriod(v as BillingPeriod)}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
              ]}
              variant={variant}
            />
          )}
        </motion.div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {activeTiers.map((tier, index) => {
            const pricing = tier.pricing[period]
            const isPopular = tier.popular

            return (
              <motion.div
                key={`${activeRole}-${tier.id}-${period}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 + index * 0.1 }}
                className="relative"
              >
                <Card
                  className={cn(
                    'h-full overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl',
                    isAi
                      ? isPopular
                        ? 'bg-panel border-2 border-brand-violet shadow-lg'
                        : 'bg-panel border border-line'
                      : isPopular
                        ? 'bg-panel border-2 border-primary-500 shadow-lg'
                        : 'bg-panel border border-line'
                  )}
                >
                  {/* Colored top bar */}
                  <div
                    className={cn(
                      'h-2 w-full',
                      isAi
                        ? 'bg-brand-violet'
                        : isPopular
                          ? 'bg-primary-500'
                          : 'bg-primary-400'
                    )}
                  />

                  <CardContent className="p-6">
                    {/* Name + badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h3
                        className={cn(
                          'font-heading font-semibold text-xl',
                          'text-ink'
                        )}
                      >
                        {tier.name}
                      </h3>
                      {tier.trialDays && (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-[10px] py-0 px-1.5',
                            isAi
                              ? 'bg-brand-violet-soft text-brand-violet border-transparent'
                              : 'bg-brand-green-soft text-brand-green border-brand-green-line'
                          )}
                        >
                          {tier.trialDays}-day free trial
                        </Badge>
                      )}
                      {isPopular && (
                        <Badge
                          variant={isAi ? 'secondary' : 'primary'}
                          className="text-[10px] py-0 px-1.5"
                        >
                          Most Popular
                        </Badge>
                      )}
                    </div>

                    {/* Price */}
                    <div className="mb-3">
                      {tier.customPriceLabel ? (
                        <span
                          className={cn(
                            'text-3xl font-bold',
                            'text-ink'
                          )}
                        >
                          {tier.customPriceLabel}
                        </span>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <span
                            className={cn(
                              'text-4xl font-bold',
                              'text-ink'
                            )}
                          >
                            {formatPrice(pricing.price)}
                          </span>
                          <span
                            className={cn(
                              'text-sm',
                              'text-muted'
                            )}
                          >
                            /{period === 'yearly' ? 'mo billed yearly' : 'mo'}
                          </span>
                        </div>
                      )}

                      {pricing.originalPrice && !tier.customPriceLabel && (
                        <div className="mt-1 flex items-center gap-2">
                          <span
                            className={cn(
                              'text-sm line-through',
                              'text-muted'
                            )}
                          >
                            {formatPrice(pricing.originalPrice)}/mo
                          </span>
                          {'savePercent' in pricing &&
                            pricing.savePercent != null && (
                              <span className="text-xs font-semibold text-brand-green bg-brand-green-soft px-1.5 py-0.5 rounded">
                                Save {pricing.savePercent}%
                              </span>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Commission / value note */}
                    {typeof tier.commission === 'number' && (
                      <div
                        className={cn(
                          'flex items-center gap-1.5 text-xs font-medium mb-4',
                          'text-muted'
                        )}
                      >
                        {tier.commission === 0
                          ? 'Zero commission — keep 100% of revenue'
                          : `+ ${tier.commission}% commission on AI-driven sales`}
                        <Info className="w-3.5 h-3.5 opacity-60" />
                      </div>
                    )}

                    {/* Description box */}
                    <div
                      className={cn(
                        'rounded-xl p-4 mb-6 text-sm leading-relaxed',
                        isAi
                          ? 'bg-brand-violet-soft text-brand-violet'
                          : 'bg-brand-green-soft text-brand-green'
                      )}
                    >
                      {tier.description}
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {tier.features.slice(0, 4).map((feature) => (
                        <li key={feature} className="flex items-start gap-2.5">
                          <Check
                            className={cn(
                              'w-5 h-5 mt-0.5 flex-shrink-0',
                              'text-brand-green'
                            )}
                          />
                          <span
                            className={cn(
                              'text-sm',
                              'text-muted'
                            )}
                          >
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Button
                      href={tier.cta.href}
                      external={tier.cta.href.startsWith('http')}
                      variant={
                        isAi
                          ? 'ai'
                          : isPopular
                            ? 'primary'
                            : 'secondary'
                      }
                      className={cn(
                        'w-full',
                        !isPopular &&
                          !isAi &&
                          'bg-panel border-line-strong text-ink hover:bg-page hover:border-line-strong'
                      )}
                    >
                      {tier.cta.text}
                      <ArrowRight className="w-4 h-4" />
                    </Button>

                    {tier.footerNote && (
                      <p
                        className={cn(
                          'text-xs text-center mt-3',
                          'text-muted'
                        )}
                      >
                        {tier.footerNote}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>

        {/* Comparison table */}
        <PricingComparisonTable tiers={activeTiers} variant={variant} />
      </Container>
    </Section>
  )
}

interface SegmentedControlProps {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  variant?: 'primary' | 'ai'
}

function SegmentedControl({
  value,
  onChange,
  options,
  variant = 'primary',
}: SegmentedControlProps) {
  const isAi = variant === 'ai'

  return (
    <div
      className={cn(
        'inline-flex p-1 rounded-xl border',
        isAi
          ? 'bg-panel border-line'
          : 'bg-raised border-line'
      )}
    >
      {options.map((option) => {
        const active = value === option.value
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            className={cn(
              'relative px-4 py-1.5 text-sm font-medium rounded-lg transition-colors',
              active
                ? isAi
                  ? 'bg-raised text-ink'
                  : 'bg-panel text-ink shadow-sm'
                : isAi
                  ? 'text-muted hover:text-ink'
                  : 'text-muted hover:text-ink'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
